import { CSSProperties, useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputTextarea } from "primereact/inputtextarea";
import { baseUrl, Fetch } from "../../shared/Fetch";
import { FileUpload, FileUploadHandlerParam } from "primereact/fileupload";

/* ---------- Types ---------- */

interface BranchMetrics {
  branchName: string;
  reqs: number;
  subs: number;
  ecs: number;
  eci: number;
  po: number;
}

interface TopBranchBannerProps {
  data: BranchMetrics;
  periodLabel: string;
  metricLabel?: string;
  toast?: any;
}

interface QuoteRecord {
  id: number;
  quotes: string;
  branch: string;
  picPath?: string;
}

const fmtInt = (n: number): string => Math.round(n).toLocaleString("en-IN");

const STATS = [
  { key: "reqs", label: "Requirements", icon: "pi pi-briefcase", color: "#17509D", bg: "#EBF1FD" },
  { key: "subs", label: "Submissions", icon: "pi pi-file", color: "#1D7358", bg: "#E1F5EE" },
  { key: "ecs", label: "ECS", icon: "pi pi-check-square", color: "#90365C", bg: "#FBEAF0" },
  { key: "eci", label: "ECI", icon: "pi pi-user-plus", color: "#854110", bg: "#FAEEDA" },
  { key: "po", label: "Purchase Ordered", icon: "pi pi-flag", color: "#3C3489", bg: "#EEEDFE" },
] as const;

/* ---------- Rosette medal---------- */

const RosetteMedallion = () => {
  const petals = 20;
  const petal = (i: number) => {
    const angle = (360 / petals) * i;
    return <ellipse key={i} cx="0" cy="-33" rx="7.5" ry="15" fill="url(#petalGrad)" transform={`rotate(${angle})`} />;
  };

  return (
    <div style={{ position: "relative", flexShrink: 0, transform: "scale(0.75)", transformOrigin: "center" }}>
      <svg width="68" height="90" viewBox="0 0 92 104">
        <defs>
          <linearGradient id="petalGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F4D68C" />
            <stop offset="100%" stopColor="#B9832E" />
          </linearGradient>
          <linearGradient id="discGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#F9E3AC" />
            <stop offset="100%" stopColor="#C99A45" />
          </linearGradient>
          <linearGradient id="tailGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#B9832E" />
            <stop offset="100%" stopColor="#8C6423" />
          </linearGradient>
        </defs>
        <path d="M34 70 L28 118 L46 98 L64 118 L58 70 L46 82 Z" fill="url(#tailGrad)" />
        <g transform="translate(46,46)">{Array.from({ length: petals }, (_, i) => petal(i))}</g>
        <circle cx="46" cy="46" r="26" fill="url(#discGrad)" stroke="#fff" strokeWidth="3" />
        <circle cx="46" cy="46" r="19.5" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1.25" strokeDasharray="1.5 3.5" />
        <path d="M46,36 L49.5,43 L57,44 L51.5,49 L53,56.5 L46,52.7 L39,56.5 L40.5,49 L35,44 L42.5,43 Z" fill="#fff" />
      </svg>
      <div
        style={{
          position: "absolute",
          bottom: 8,
          right: -3,
          width: 28,
          height: 28,
          borderRadius: "50%",
          background: "#3C3489",
          border: "3px solid #fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 13,
          fontWeight: 800,
          color: "#fff",
          boxShadow: "0 2px 6px rgba(60,52,137,0.35)",
        }}
      >
        1
      </div>
    </div>
  );
};

/* ---------- Stat mini-card (unchanged) ---------- */

const StatCard = ({ value, label, icon, color, bg }: { value: number; label: string; icon: string; color: string; bg: string }) => (
  <div style={{ position: "relative", flex: "1 1 130px", minWidth: 130, background: `linear-gradient(135deg, ${bg} 0%, #ffffff 78%)`, borderRadius: 12, padding: "14px 16px", overflow: "hidden", boxShadow: "0 1px 2px rgba(16,24,40,0.05)" }}>
    <div style={{ position: "absolute", top: 0, left: 0, bottom: 0, width: 3.5, background: color }} />
    <i className={icon} aria-hidden="true" style={{ position: "absolute", right: -6, bottom: -10, fontSize: 56, color, opacity: 0.12, transform: "rotate(-8deg)", pointerEvents: "none" }} />
    <div style={{ position: "relative", paddingLeft: 6 }}>
      <span style={{ display: "block", fontSize: 10.5, fontWeight: 700, letterSpacing: "0.04em", color, textTransform: "uppercase", marginBottom: 8 }}>{label}</span>
      <span style={{ fontSize: 30, fontWeight: 800, color: "#14181F", lineHeight: 1, letterSpacing: "-0.5px" }}>{fmtInt(value)}</span>
    </div>
  </div>
);

/* ---------- Main banner ---------- */

export const TopBranchBanner = ({ toast, data, periodLabel, metricLabel = "performance" }: TopBranchBannerProps) => {
  const periodWord = periodLabel.replace("'s", "").toLowerCase();

  const [quote, setQuote] = useState<QuoteRecord | null>(null);
  const [isBranchHead, setIsBranchHead] = useState(false);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [quoteInput, setQuoteInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState<"quote" | "image">("quote");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const imageUploadHandler = async (event: FileUploadHandlerParam) => {
    const file = event.files[0];

    if (file.size > 2000000) {
      toast.current.show({ severity: "error", content: `${file.name} Image should be less than 2 MB.` });
      event.options.clear();
      return;
    }

    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    event.options.clear();
  };

  const fetchQuote = async () => {
    const response = await Fetch(`CreativityDashboard/GetQuote`, "GET", null, `branch=${encodeURIComponent(data.branchName)}`, null);
    if (response) {
      const result = await response.json();
      setQuote(result ?? null);
      setDialogVisible(false);
    }
  };

  const checkBranchHead = async () => {
    const response = await Fetch(`CreativityDashboard/IsTopBranchHead`, "GET", null, `branch=${encodeURIComponent(data.branchName)}`, null);
    if (response) {
      const result = await response.json();
      setIsBranchHead(!!result?.isBranchHead);
    }
  };

  const openDialog = () => {
    setMode(quote?.picPath ? "image" : "quote");
    setQuoteInput(quote?.quotes ?? "");
    setImageFile(null);
    setImagePreview(null);
    setDialogVisible(true);
  };

  useEffect(() => {
    if (!quote?.picPath) {
      setImageSrc(null);
      return;
    }

    let objectUrl: string;
    setImageLoading(true);

    Fetch(`CreativityDashboard/GetQuoteImage/${quote.id}`, "GET", null, null, null)
      .then((res) => {
        if (!res) throw new Error("failed");
        return res.blob();
      })
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setImageSrc(objectUrl);
      })
      .catch(() => setImageSrc(null))
      .finally(() => setImageLoading(false));

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [quote?.picPath, quote?.id]);

  useEffect(() => {
    if (!data?.branchName) return;
    fetchQuote();
    checkBranchHead();
  }, [data?.branchName]);

  const saveQuote = async () => {
    if (mode === "quote" && !quoteInput.trim()) return;
    if (mode === "image" && !imageFile && !quote?.picPath) return;

    setSaving(true);

    const form = new FormData();
    form.append("Branch", data.branchName);
    if (mode === "quote") {
      form.append("Quote", quoteInput.trim());
    } else if (imageFile) {
      form.append("Image", imageFile);
    }

    const url = quote ? `${baseUrl}CreativityDashboard/EditQuote/${quote.id}` : `${baseUrl}CreativityDashboard/AddQuote`;
    const method = quote ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method,
        headers: {
          Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("auth") + "")?.token}`,
        },
        body: form,
      });

      if (response.status === 401) {
        sessionStorage.clear();
        window.location.href = "/login";
        return;
      } else if (response.status === 400) {
        const message = await response.text();
        toast?.current?.show({ severity: "error", detail: message, life: 3000 });
        return;
      } else if (response.status !== 200) {
        toast?.current?.show({ severity: "error", detail: "Something went Wrong", life: 3000 });
        return;
      }

      const result = await response.json();
      setQuote(result);
      setDialogVisible(false);
    } catch {
      toast?.current?.show({ severity: "error", detail: "Something went Wrong", life: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const outerStyle: CSSProperties = {
    position: "relative",
    marginBottom: "1.5rem",
  };

  const cardStyle: CSSProperties = {
    position: "relative",
    background: "linear-gradient(180deg, #FFFFFF 0%, #FEFCF6 100%)",
    border: "1px solid #EEE2C8",
    borderRadius: 16,
    padding: "1.75rem 2rem 1.5rem",
    overflow: "hidden",
    boxShadow: "0 8px 24px rgba(185,131,46,0.10), 0 1px 3px rgba(16,24,40,0.05)",
  };

  return (
    <div className="jv-top-branch-banner" style={outerStyle}>
      <div style={cardStyle}>
        {/* gold accent bar */}
        <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 4, background: "linear-gradient(90deg, #F4D68C, #B9832E, #F4D68C)" }} />
        {/* soft corner glow */}
        <div aria-hidden="true" style={{ position: "absolute", top: -70, right: -70, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(201,162,39,0.12) 0%, rgba(201,162,39,0) 70%)", pointerEvents: "none" }} />
        {/* top row: medallion  */}
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: 20, marginBottom: 22 }}>
          <RosetteMedallion />
          <div style={{ minWidth: 0 }}>
            <span style={{ display: "inline-block", fontSize: 12, fontWeight: 800, letterSpacing: "0.1em", textTransform: "uppercase", color: "#B9832E", marginBottom: 6 }}> Top Performer · {periodLabel.replace(/'s$/, "")}</span>
            <p style={{ fontSize: 26, lineHeight: 1.3, margin: 0, color: "#14181F" }}>
              <strong style={{ fontWeight: 800 }}>{data.branchName}</strong> achieved the highest {metricLabel} {periodWord}. Great job!
            </p>
          </div>
        </div>
        {/* stat cards */}
        <div className="jv-top-branch-stats" style={{ position: "relative", zIndex: 1, display: "flex", gap: 12, flexWrap: "wrap" }}>
          {STATS.map((s) => (
            <StatCard key={s.key} value={data[s.key]} label={s.label} icon={s.icon} color={s.color} bg={s.bg} />
          ))}
        </div>
        {/* motivational quote */}
        {(quote?.quotes || quote?.picPath) && (
          <div style={{ position: "relative", zIndex: 1, marginTop: 24, padding: "22px 28px 20px", textAlign: "center", background: "linear-gradient(135deg, #FEFBF3 0%, #FDF6E7 100%)", border: "1px solid #F0E2C0", borderRadius: 14, overflow: "hidden" }}>
            {quote.picPath ? (
              <img src={imageSrc ?? undefined} alt={`${data.branchName} quote`} style={{ display: imageLoading ? "none" : "block", width: 320, height: 220, objectFit: "contain", borderRadius: 10, boxShadow: "0 4px 14px rgba(185,131,46,0.18)", margin: "0 auto",    }} />
            ) : (
              <>
                <span aria-hidden="true" style={{ position: "absolute", top: -18, left: "50%", transform: "translateX(-50%)", fontSize: 96, lineHeight: 1, fontFamily: "Georgia, 'Times New Roman', serif", fontWeight: 700, color: "#B9832E", opacity: 0.12, pointerEvents: "none", userSelect: "none" }}>
                  "
                </span>
                <p style={{ position: "relative", margin: "0 auto", maxWidth: 480, fontSize: 15.5, fontStyle: "italic", fontWeight: 500, color: "#3F3A2E", lineHeight: 1.6 }}>{quote.quotes}</p>
              </>
            )}

            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 8, marginTop: 12 }}>
              <span style={{ width: 16, height: 1.5, background: "#B9832E", opacity: 0.5 }} />
              <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: "#B9832E" }}>{data.branchName} Branch</span>
              <span style={{ width: 16, height: 1.5, background: "#B9832E", opacity: 0.5 }} />
            </div>
          </div>
        )}
      </div>

      {/* Quote Button */}
      {isBranchHead && (
        <button
          type="button"
          onClick={openDialog}
          title={quote ? "Edit branch quote" : "Add branch quote"}
          style={{
            position: "absolute",
            bottom: -18,
            right: 24,
            zIndex: 2,
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "3px solid #fff",
            background: "#3C3489",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            boxShadow: "0 2px 6px rgba(60,52,137,0.35)",
          }}
        >
          <i className={quote ? "pi pi-pencil" : "pi pi-plus"} style={{ fontSize: 14 }} />
        </button>
      )}

      <Dialog header={quote ? "Edit Branch Quote" : "Add Branch Quote"} visible={dialogVisible} style={{ width: "28rem" }} onHide={() => setDialogVisible(false)}>
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <Button label="Quote" className={mode !== "quote" ? "p-button-outlined" : ""} onClick={() => setMode("quote")} style={{ flex: 1 }} />
            <Button label="Image" className={mode !== "image" ? "p-button-outlined" : ""} onClick={() => setMode("image")} style={{ flex: 1 }} />
          </div>
        </div>

        {mode === "quote" ? (
          <InputTextarea value={quoteInput} onChange={(e) => setQuoteInput(e.target.value)} rows={4} autoResize maxLength={250} placeholder="Share a motivational line for the branch..." style={{ width: "100%" }} />
        ) : (
          <div className="p-2 pl-3" style={{ border: "1px solid rgb(210, 208, 208)", textAlign: "center" }}>
            <span id="file" style={{ width: "15rem" }}>
              Branch Image <span style={{ color: "red" }}>*</span>
            </span>
            <FileUpload chooseLabel={"Choose Image"} mode="basic" name="file" accept="image/*" maxFileSize={2000000} auto customUpload uploadHandler={imageUploadHandler} />

            {(imagePreview || quote?.picPath) && <img src={imagePreview ?? `${baseUrl}${quote?.picPath?.replace(/\\/g, "/")}`} alt="preview" style={{ maxWidth: "100%", maxHeight: 180, marginTop: 12, borderRadius: 8, objectFit: "contain" }} />}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <Button label="Cancel" onClick={() => setDialogVisible(false)} disabled={saving} />
          <Button label="Save" onClick={saveQuote} loading={saving} disabled={mode === "quote" ? !quoteInput.trim() : !imageFile && !quote?.picPath} />
        </div>
      </Dialog>
    </div>
  );
};
